import { useParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronDown, MapPin, Calendar, Users, Clock, Building, Send, ExternalLink, Check, Lock, Unlock, Filter, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import SEO from '@/components/SEO';
import { useOpportunityApi, useSimilarOpportunities } from "@/hooks/useOpportunityApi";
import { Opportunity } from "@/hooks/useOpportunitiesApi";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  useNPOsApi, 
  useInfiniteNPOsApi, 
  useNPOTypes, 
} from "@/hooks/useNPOsApi";
import { Checkbox } from "@/components/ui/checkbox";
import { useInView } from "react-intersection-observer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { NPO, useNPOCountries, useNPOStates, useNPODistricts } from "@/hooks/useNPOsApi";
import { useEffect, useState } from "react";

// Function to generate a consistent color based on a string
function stringToColor(str: string) {
  const colors = [
    "from-pink-500 to-rose-500",
    "from-purple-500 to-indigo-500",
    "from-blue-500 to-cyan-500",
    "from-emerald-500 to-green-500",
    "from-amber-500 to-yellow-500",
    "from-orange-500 to-red-500",
    "from-fuchsia-500 to-pink-500",
    "from-sky-500 to-blue-500",
    "from-teal-500 to-emerald-500",
    "from-lime-500 to-green-500",
  ];
  
  // Simple hash function to get a consistent index
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const index = Math.abs(hash) % colors.length;
  return colors[index];
}

function OpportunityCard({ 
  opportunity,
  showLocation = false,
}: { 
  opportunity: Opportunity;
  showLocation?: boolean;
}) {
  const gradientClass = stringToColor(opportunity.name);
  
  return (
    <Link
      to={`/opportunities/${opportunity.id}`}
      className="block group"
    >
      <Card className="overflow-hidden h-full hover:shadow-lg transition-shadow rounded-xl">
        <div className="aspect-[4/3] relative overflow-hidden rounded-t-xl">
          {opportunity.image_url ? (
            <img
              src={opportunity.image_url}
              alt={opportunity.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div 
              className={cn(
                "w-full h-full flex items-center justify-center group-hover:scale-105 transition-transform duration-300",
                "bg-gradient-to-br", gradientClass
              )}
            >
              <span className="text-white text-2xl font-medium">
                {opportunity.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </div>
        <div className="p-4">
          <h3 className="font-semibold text-lg mb-2">
            {opportunity.name}
          </h3>
          {showLocation && opportunity.location && (
            <div className="text-sm text-gray-600 mb-2">
              {opportunity.location}
            </div>
          )}
          <p className="text-gray-600 text-sm line-clamp-2">
            {opportunity.community_need}
          </p>
        </div>
      </Card>
    </Link>
  );
}

function NPOCard({ 
  npo, 
  isSelected, 
  onToggleSelect 
}: { 
  npo: NPO, 
  isSelected: boolean, 
  onToggleSelect: (npo: NPO) => void 
}) {
  const gradientClass = stringToColor(npo.name);
  
  return (
    <Card 
      className={cn(
        "overflow-hidden transition-all border-2 cursor-pointer",
        isSelected ? "border-primary shadow-md" : "border-transparent hover:border-gray-200"
      )}
      onClick={() => onToggleSelect(npo)}
    >
      <div className="p-4 flex items-start gap-4">
        <Checkbox 
          checked={isSelected}
          onCheckedChange={() => onToggleSelect(npo)}
          className="mt-1"
          onClick={(e) => e.stopPropagation()} // Prevent double toggle when clicking checkbox directly
        />
        <div className={cn(
          "w-12 h-12 flex-shrink-0 rounded-full flex items-center justify-center",
          "bg-gradient-to-br", gradientClass
        )}>
          <span className="text-white text-lg font-medium">
            {npo.name.charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-lg">{npo.name}</h3>
          <p className="text-gray-600 text-sm mb-2">{npo.description}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-500 mb-2">
            {npo.meta?.state_name && (
              <div className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                <span>{npo.meta.state_name}</span>
              </div>
            )}
            {npo.meta?.district_name && (
              <div className="flex items-center gap-1">
                <Building className="h-3 w-3" />
                <span>{npo.meta.district_name}</span>
              </div>
            )}
            {npo.meta?.website && (
              <div className="flex items-center gap-1 col-span-2">
                <ExternalLink className="h-3 w-3" />
                <a 
                  href={npo.meta.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                  onClick={(e) => e.stopPropagation()} // Prevent card selection when clicking the link
                >
                  {npo.meta.website.replace(/^https?:\/\//, '')}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

// RFQ Form Component
function RFQForm({ onSubmit, onClose, selectedNPOs = [], profile }: { 
  onSubmit: (data: any) => void; 
  onClose: () => void;
  selectedNPOs?: NPO[];
  profile?: any;
}) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: `${profile?.first_name} ${profile?.last_name}`,
    email: profile?.email || "",
    company: profile?.org_name || "",
    phone: profile?.org_phone || "",
    participantCount: "",
    date: "",
    time: "",
    timezone: "GMT+8",
    country: "India",
    state: "",
    district: "",
    additionalRequests: ""
  });

  // Fetch location data
  const { data: countriesData } = useNPOCountries();
  const { data: statesData } = useNPOStates(formData.country || undefined);
  const { data: districtsData } = useNPODistricts(formData.state || undefined, formData.country || undefined);

  // Validate form fields for each step
  const isStep1Valid = () => {
    const requiredFields = ['name', 'email', 'company', 'phone'];
    return requiredFields.every(field => !!formData[field as keyof typeof formData]);
  };

  // Auto-advance to step 2 if all step 1 fields are already filled
  useEffect(() => {
    if (step === 1 && isStep1Valid()) {
      setStep(2);
    }
  }, [step, isStep1Valid]);

  // Ensure we always have at least one country option
  const countries = (countriesData?.data?.length ? countriesData.data : ["India"]);
  const states = statesData?.data || [];
  const districts = districtsData?.data || [];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      
      // Reset dependent fields when parent field changes
      if (name === 'country') {
        newData.state = '';
        newData.district = '';
      } else if (name === 'state') {
        newData.district = '';
      }
      
      return newData;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const isStep2Valid = () => {
    const requiredFields = ['date', 'time'];
    return requiredFields.every(field => !!formData[field as keyof typeof formData]);
  };

  const isFormValid = () => {
    const requiredFields = ['name', 'email', 'company', 'phone', 'participantCount', 'date', 'time'];
    return requiredFields.every(field => !!formData[field as keyof typeof formData]);
  };

  const nextStep = () => {
    if (step === 1 && isStep1Valid()) {
      setStep(2);
    } else if (step === 2 && isStep2Valid()) {
      setStep(3);
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Step indicator */}
      <div className="flex justify-between mb-6">
        <div className={`flex flex-col items-center ${step >= 1 ? 'text-primary' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${step >= 1 ? 'bg-primary text-white' : 'bg-gray-200'}`}>1</div>
          <span className="text-xs">Your Details</span>
        </div>
        <div className="flex-1 flex items-center">
          <div className={`h-1 w-full ${step >= 2 ? 'bg-primary' : 'bg-gray-200'}`}></div>
        </div>
        <div className={`flex flex-col items-center ${step >= 2 ? 'text-primary' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${step >= 2 ? 'bg-primary text-white' : 'bg-gray-200'}`}>2</div>
          <span className="text-xs">Location & Time</span>
        </div>
        <div className="flex-1 flex items-center">
          <div className={`h-1 w-full ${step >= 3 ? 'bg-primary' : 'bg-gray-200'}`}></div>
        </div>
        <div className={`flex flex-col items-center ${step >= 3 ? 'text-primary' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${step >= 3 ? 'bg-primary text-white' : 'bg-gray-200'}`}>3</div>
          <span className="text-xs">Event Details</span>
        </div>
      </div>

      {/* Step 1: Your Details */}
      {step === 1 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Your Details</h3>
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company">Company</Label>
            <Input
              id="company"
              name="company"
              required
              value={formData.company}
              onChange={handleChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              name="phone"
              required
              value={formData.phone}
              onChange={handleChange}
            />
          </div>
        </div>
      )}

      {/* Step 2: Location & Time */}
      {step === 2 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Location & Time</h3>
          
          {/* Location Fields */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium">Location Details</h4>
            
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Select name="country" value={formData.country} onValueChange={(value) => handleChange({ target: { name: 'country', value } } as any)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a country" />
                </SelectTrigger>
                <SelectContent>
                  {countries.length > 0 ? (
                    countries.map(country => (
                      <SelectItem key={country} value={country || `country-${country}`}>{country}</SelectItem>
                    ))
                  ) : (
                    <SelectItem value="default-country" disabled>No countries available</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="state">State/Province</Label>
              <Select 
                name="state" 
                value={formData.state} 
                onValueChange={(value) => handleChange({ target: { name: 'state', value } } as any)}
                disabled={!formData.country}
              >
                <SelectTrigger>
                  <SelectValue placeholder={formData.country ? "Select a state" : "Select a country first"} />
                </SelectTrigger>
                <SelectContent>
                  {states.length > 0 ? (
                    states.map(state => (
                      <SelectItem key={state} value={state || `state-${state}`}>{state}</SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-states-available" disabled>No states available</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="district">City/District</Label>
              <Select 
                name="district" 
                value={formData.district} 
                onValueChange={(value) => handleChange({ target: { name: 'district', value } } as any)}
                disabled={!formData.state}
              >
                <SelectTrigger>
                  <SelectValue placeholder={formData.state ? "Select a city/district" : "Select a state first"} />
                </SelectTrigger>
                <SelectContent>
                  {districts.length > 0 ? (
                    districts.map(district => (
                      <SelectItem key={district} value={district || `district-${district}`}>{district}</SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-districts-available" disabled>No districts available</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Time Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                name="date"
                type="date"
                required
                value={formData.date}
                onChange={handleChange}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="time">Time</Label>
              <Input
                id="time"
                name="time"
                type="time"
                required
                value={formData.time}
                onChange={handleChange}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="timezone">Timezone</Label>
            <select
              id="timezone"
              name="timezone"
              className="w-full rounded-md border border-input bg-background px-3 py-2"
              value={formData.timezone}
              onChange={handleChange}
            >
              <option value="GMT-12">GMT-12:00</option>
              <option value="GMT-11">GMT-11:00</option>
              <option value="GMT-10">GMT-10:00</option>
              <option value="GMT-9">GMT-09:00</option>
              <option value="GMT-8">GMT-08:00</option>
              <option value="GMT-7">GMT-07:00</option>
              <option value="GMT-6">GMT-06:00</option>
              <option value="GMT-5">GMT-05:00</option>
              <option value="GMT-4">GMT-04:00</option>
              <option value="GMT-3">GMT-03:00</option>
              <option value="GMT-2">GMT-02:00</option>
              <option value="GMT-1">GMT-01:00</option>
              <option value="GMT+0">GMT+00:00</option>
              <option value="GMT+1">GMT+01:00</option>
              <option value="GMT+2">GMT+02:00</option>
              <option value="GMT+3">GMT+03:00</option>
              <option value="GMT+4">GMT+04:00</option>
              <option value="GMT+5">GMT+05:00</option>
              <option value="GMT+5:30">GMT+05:30 (IST)</option>
              <option value="GMT+6">GMT+06:00</option>
              <option value="GMT+7">GMT+07:00</option>
              <option value="GMT+8">GMT+08:00</option>
              <option value="GMT+9">GMT+09:00</option>
              <option value="GMT+10">GMT+10:00</option>
              <option value="GMT+11">GMT+11:00</option>
              <option value="GMT+12">GMT+12:00</option>
            </select>
          </div>
        </div>
      )}

      {/* Step 3: Event Details */}
      {step === 3 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Event Details</h3>
          
          <div className="space-y-2">
            <Label htmlFor="participantCount">Number of Participants</Label>
            <Input
              id="participantCount"
              name="participantCount"
              type="number"
              required
              value={formData.participantCount}
              onChange={handleChange}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="additionalRequests">Additional Requests</Label>
            <Textarea
              id="additionalRequests"
              name="additionalRequests"
              placeholder="Any specific requirements or questions..."
              value={formData.additionalRequests}
              onChange={handleChange}
            />
          </div>
          
          {selectedNPOs.length > 0 && (
            <div className="space-y-2">
              <Label>Selected NPOs ({selectedNPOs.length})</Label>
              <div className="bg-gray-50 p-2 rounded-md max-h-40 overflow-y-auto">
                <ul className="space-y-1">
                  {selectedNPOs.map(npo => (
                    <li key={npo.id} className="text-sm">{npo.name}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
      
      <DialogFooter>
        {step > 1 && (
          <Button type="button" variant="outline" onClick={prevStep}>
            Back
          </Button>
        )}
        
        {step < 3 ? (
          <Button 
            type="button" 
            onClick={nextStep}
            disabled={(step === 1 && !isStep1Valid()) || (step === 2 && !isStep2Valid())}
          >
            Next
          </Button>
        ) : (
          <>
            <Button type="button" variant="outline" onClick={onClose} className="mr-2">
              Cancel
            </Button>
            <Button type="submit" disabled={!isFormValid()}>Submit Request</Button>
          </>
        )}
      </DialogFooter>
    </form>
  );
}

export default function SkuOpportunityDetailsPage({profile, session}: {profile: any | null; session: any | null}) {
  const { id } = useParams();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedNPOs, setSelectedNPOs] = useState<NPO[]>([]);
  const [rfqDialogOpen, setRfqDialogOpen] = useState(false);
  const [rfqFormCompleted, setRfqFormCompleted] = useState(false);
  const [initialRfqData, setInitialRfqData] = useState<any>(null);
  const [npoFilters, setNpoFilters] = useState<{
    country: string | null;
    type: string | null;
    state: string | null;
    district: string | null;
  }>({
    country: null,
    type: null,
    state: null,
    district: null
  });

  // Create ref for intersection observer for infinite scroll
  const { ref, inView } = useInView({
    threshold: 0.1,
    rootMargin: "0px 0px 200px 0px" // Load more when user is 200px from bottom
  });

  const { data, isLoading, error } = useOpportunityApi(id || "");
  const opportunity = data?.data;

  // Fetch countries and types for filtering
  const { data: countriesData, isLoading: isLoadingCountries } = useNPOCountries();
  const { data: typesData, isLoading: isLoadingTypes } = useNPOTypes();
  
  // Fetch states based on selected country
  const { data: statesData, isLoading: isLoadingStates } = useNPOStates(
    npoFilters.country || undefined
  );
  
  // Fetch districts based on selected state and country
  const { data: districtsData, isLoading: isLoadingDistricts } = useNPODistricts(
    npoFilters.state || undefined,
    npoFilters.country || undefined
  );
  
  // Ensure we always have at least one country option
  const countries = (countriesData?.data?.length ? countriesData.data : ["India"]);
  const types = typesData?.data || [];
  const states = statesData?.data || [];
  const districts = districtsData?.data || [];

  // Only fetch NPOs if RFQ form is completed
  const { 
    data: nposData, 
    isLoading: isLoadingNPOs,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchNPOs
  } = useInfiniteNPOsApi({
    limit: 10,
    country: npoFilters.country || undefined,
    type: npoFilters.type || undefined,
    meta: {
      ...(npoFilters.state ? { state_name: npoFilters.state } : {}),
      ...(npoFilters.district ? { district_name: npoFilters.district } : {})
    }
  }, {
    enabled: rfqFormCompleted // Only fetch if RFQ form is completed
  });
  
  // Flatten all NPOs from all pages
  const npos = nposData?.pages.flatMap(page => page.data) || [];
  const totalNPOsCount = nposData?.pages[0]?.pagination.total || 0;

  // Effect to fetch next page when user scrolls to bottom
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && rfqFormCompleted) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage, hasNextPage, isFetchingNextPage, rfqFormCompleted]);

  // Effect to refetch NPOs when filters change
  useEffect(() => {
    if (rfqFormCompleted) {
      refetchNPOs();
    }
  }, [npoFilters, refetchNPOs, rfqFormCompleted]);

  // Function to format the NPO count in a human-readable way
  const formatNPOCount = (count: number) => {
    if (count === 0) return "No NPOs available";
    if (count === 1) return "1 NPO available";
    return `${count} NPOs available`;
  };

  const handleFilterChange = (filterType: 'country' | 'type' | 'state' | 'district', value: string | null) => {
    setNpoFilters(prev => ({
      ...prev,
      [filterType]: value,
      // Reset dependent fields when parent field changes
      ...(filterType === 'country' ? { state: null, district: null } : {}),
      ...(filterType === 'state' ? { district: null } : {})
    }));
  };

  const clearFilters = () => {
    setNpoFilters({
      country: null,
      type: null,
      state: null,
      district: null
    });
  };

  const hasActiveFilters = npoFilters.country !== null || npoFilters.type !== null || 
                          npoFilters.state !== null || npoFilters.district !== null;

  const { data: similarOpportunitiesData } = useSimilarOpportunities(
    id || "", 
    opportunity?.focus_area || "",
    3
  );
  
  const similarOpportunities = similarOpportunitiesData?.data || [];

  const handleToggleSelectNPO = (npo: NPO) => {
    setSelectedNPOs(prev => {
      const isAlreadySelected = prev.some(item => item.id === npo.id);
      if (isAlreadySelected) {
        return prev.filter(item => item.id !== npo.id);
      } else {
        return [...prev, npo];
      }
    });
  };

  const handleRequestQuote = () => {
    if (selectedNPOs.length > 0) {
      setRfqDialogOpen(true);
    }
  };

  const handleRfqSubmit = (formData: any) => {
    // Store the form data for future use
    setInitialRfqData(formData);
    
    // Set filters based on form data
    setNpoFilters({
      country: formData.country || null,
      type: null, // Keep the type filter as is
      state: formData.state || null,
      district: formData.district || null
    });
    
    // Close the dialog
    setRfqDialogOpen(false);
    
    // Mark the RFQ form as completed to enable NPO loading
    setRfqFormCompleted(true);
  };

  const handleInitialRfqSubmit = () => {
    // Open the RFQ dialog
    setRfqDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (error || !opportunity) {
    return (
      <div className="container mx-auto py-8">
        <Card className="p-6">
          <h1 className="text-2xl font-bold">Opportunity not found</h1>
          <p className="text-gray-600 mt-2">
            The opportunity you're looking for doesn't exist or has been removed.
          </p>
          <Button asChild className="mt-4">
            <Link to="/opportunities">Back to Opportunities</Link>
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={opportunity.name || "Volunteer Opportunity"}
        description={opportunity.community_need || "Learn more about this volunteer opportunity and how you can make a difference."}
      />
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-4">
          <div className="text-sm text-gray-600">
            <Link to="/opportunities" className="hover:text-blue-600">Opportunities</Link>
            <span className="mx-2">/</span>
            <span>{opportunity.name}</span>
          </div>
        </div>

        <div className="container mx-auto px-4 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left column - Opportunity details */}
            <div className="lg:col-span-2">
              <div className="flex gap-6 mb-6">
                {opportunity.image_url ? (
                  <div className="w-32 h-32 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={opportunity.image_url}
                      alt={opportunity.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className={cn(
                    "w-32 h-32 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center",
                    "bg-gradient-to-br", stringToColor(opportunity.name)
                  )}>
                    <span className="text-white text-3xl font-bold">
                      {opportunity.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                
                <div className="flex-1">
                  <h1 className="text-2xl font-bold mb-2">{opportunity.name}</h1>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    {opportunity.location && (
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        <span>{opportunity.location}</span>
                      </div>
                    )}
                    {opportunity.format && (
                      <div className="flex items-center gap-1">
                        <Building className="h-4 w-4" />
                        <span>{opportunity.format.replace('_', ' ')}</span>
                      </div>
                    )}
                    {opportunity.focus_area && (
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        <span>{opportunity.focus_area.replace('_', ' & ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <Tabs defaultValue="details" className="mb-8">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="impact">Impact</TabsTrigger>
                  <TabsTrigger value="requirements">Requirements</TabsTrigger>
                </TabsList>
                
                <TabsContent value="details" className="p-4">
                  <div className="prose max-w-none">
                    <h3 className="text-lg font-semibold mb-2">Community Need</h3>
                    <p className="text-gray-600 whitespace-pre-wrap mb-4">{opportunity.community_need}</p>
                    
                    <h3 className="text-lg font-semibold mb-2">Volunteer Support</h3>
                    <p className="text-gray-600 whitespace-pre-wrap">{opportunity.volunteer_support}</p>
                  </div>
                </TabsContent>
                
                <TabsContent value="impact" className="p-4">
                  <div className="prose max-w-none">
                    <h3 className="text-lg font-semibold mb-2">Impact</h3>
                    <p className="text-gray-600 whitespace-pre-wrap mb-4">{opportunity.impact || "Impact information not available."}</p>
                    
                    <h3 className="text-lg font-semibold mb-2">Outcome</h3>
                    <p className="text-gray-600 whitespace-pre-wrap">{opportunity.outcome || "Outcome information not available."}</p>
                  </div>
                </TabsContent>
                
                <TabsContent value="requirements" className="p-4">
                  <div className="prose max-w-none">
                    <h3 className="text-lg font-semibold mb-2">Requirements</h3>
                    <ul className="list-disc pl-5 text-gray-600">
                      <li>Minimum age: 18 years</li>
                      <li>Time commitment: 3-4 hours</li>
                      <li>Skills needed: Basic communication</li>
                      <li>Training provided: Yes</li>
                    </ul>
                  </div>
                </TabsContent>
              </Tabs>
              
              {similarOpportunities.length > 0 && (
                <div className="mt-8">
                  <h2 className="text-xl font-semibold mb-4">Similar Opportunities</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {similarOpportunities.map((opp) => (
                      <OpportunityCard key={opp.id} opportunity={opp} showLocation />
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Right column - NPOs and RFQ */}
            <div className="lg:col-span-1">
              <Card className="p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold">Supporting NPOs</h2>
                  {!isLoadingNPOs && rfqFormCompleted && (
                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                      {formatNPOCount(totalNPOsCount)}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Select one or more nonprofit organizations and request a quote to organize this activity with your team.
                </p>
                
                {!rfqFormCompleted ? (
                  <div className="flex flex-col items-center justify-center py-8 px-4 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
                    <div className="bg-primary/10 p-3 rounded-full mb-4">
                      <Lock className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">NPO List Locked</h3>
                    <p className="text-sm text-gray-500 text-center mb-6">
                      Please provide your request details to unlock the list of nonprofit organizations available for this opportunity.
                    </p>
                    <Button 
                      onClick={handleInitialRfqSubmit}
                      className="w-full flex items-center justify-center gap-2"
                    >
                      <Unlock className="h-4 w-4" />
                      {!session ? "Login to Continue" : profile?.user_type !== 'company' ? "Corporate Account Required" : "Provide Details to Unlock"}
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* NPO Filters */}
                    <div className="mb-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-medium">Filter NPOs</h3>
                        {hasActiveFilters && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={clearFilters}
                            className="h-8 px-2 text-xs"
                          >
                            Clear all
                          </Button>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button 
                              variant={npoFilters.country ? "default" : "outline"} 
                              size="sm" 
                              className="h-8"
                            >
                              <Filter className="h-3 w-3 mr-1" />
                              Country
                              {npoFilters.country && (
                                <Badge variant="secondary" className="ml-1 bg-primary/20 hover:bg-primary/30">
                                  {npoFilters.country}
                                </Badge>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-2">
                            <div className="space-y-2">
                              <div 
                                className="px-2 py-1 cursor-pointer hover:bg-gray-100 rounded text-sm"
                                onClick={() => handleFilterChange('country', null)}
                              >
                                All Countries
                              </div>
                              {isLoadingCountries ? (
                                <div className="p-2">
                                  <Skeleton className="h-4 w-full" />
                                  <Skeleton className="h-4 w-full mt-1" />
                                  <Skeleton className="h-4 w-full mt-1" />
                                </div>
                              ) : (
                                countries.map(country => (
                                  <div 
                                    key={country}
                                    className={cn(
                                      "px-2 py-1 cursor-pointer hover:bg-gray-100 rounded text-sm",
                                      npoFilters.country === country && "bg-primary/10 font-medium"
                                    )}
                                    onClick={() => handleFilterChange('country', country)}
                                  >
                                    {country}
                                  </div>
                                ))
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                        
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button 
                              variant={npoFilters.type ? "default" : "outline"} 
                              size="sm" 
                              className="h-8"
                            >
                              <Filter className="h-3 w-3 mr-1" />
                              Type
                              {npoFilters.type && (
                                <Badge variant="secondary" className="ml-1 bg-primary/20 hover:bg-primary/30">
                                  {npoFilters.type}
                                </Badge>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-2">
                            <div className="space-y-2">
                              <div 
                                className="px-2 py-1 cursor-pointer hover:bg-gray-100 rounded text-sm"
                                onClick={() => handleFilterChange('type', null)}
                              >
                                All Types
                              </div>
                              {isLoadingTypes ? (
                                <div className="p-2">
                                  <Skeleton className="h-4 w-full" />
                                  <Skeleton className="h-4 w-full mt-1" />
                                  <Skeleton className="h-4 w-full mt-1" />
                                </div>
                              ) : (
                                types.map(type => (
                                  <div 
                                    key={type}
                                    className={cn(
                                      "px-2 py-1 cursor-pointer hover:bg-gray-100 rounded text-sm",
                                      npoFilters.type === type && "bg-primary/10 font-medium"
                                    )}
                                    onClick={() => handleFilterChange('type', type)}
                                  >
                                    {type}
                                  </div>
                                ))
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                        
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button 
                              variant={npoFilters.state ? "default" : "outline"} 
                              size="sm" 
                              className="h-8"
                            >
                              <Filter className="h-3 w-3 mr-1" />
                              State
                              {npoFilters.state && (
                                <Badge variant="secondary" className="ml-1 bg-primary/20 hover:bg-primary/30">
                                  {npoFilters.state}
                                </Badge>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-2">
                            <div className="space-y-2">
                              <div 
                                className="px-2 py-1 cursor-pointer hover:bg-gray-100 rounded text-sm"
                                onClick={() => handleFilterChange('state', null)}
                              >
                                All States
                              </div>
                              {isLoadingStates ? (
                                <div className="p-2">
                                  <Skeleton className="h-4 w-full" />
                                  <Skeleton className="h-4 w-full mt-1" />
                                  <Skeleton className="h-4 w-full mt-1" />
                                </div>
                              ) : (
                                states.map(state => (
                                  <div 
                                    key={state}
                                    className={cn(
                                      "px-2 py-1 cursor-pointer hover:bg-gray-100 rounded text-sm",
                                      npoFilters.state === state && "bg-primary/10 font-medium"
                                    )}
                                    onClick={() => handleFilterChange('state', state)}
                                  >
                                    {state}
                                  </div>
                                ))
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                        
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button 
                              variant={npoFilters.district ? "default" : "outline"} 
                              size="sm" 
                              className="h-8"
                              disabled={!npoFilters.state}
                            >
                              <Filter className="h-3 w-3 mr-1" />
                              District
                              {npoFilters.district && (
                                <Badge variant="secondary" className="ml-1 bg-primary/20 hover:bg-primary/30">
                                  {npoFilters.district}
                                </Badge>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-2">
                            <div className="space-y-2">
                              <div 
                                className="px-2 py-1 cursor-pointer hover:bg-gray-100 rounded text-sm"
                                onClick={() => handleFilterChange('district', null)}
                              >
                                All Districts
                              </div>
                              {isLoadingDistricts ? (
                                <div className="p-2">
                                  <Skeleton className="h-4 w-full" />
                                  <Skeleton className="h-4 w-full mt-1" />
                                  <Skeleton className="h-4 w-full mt-1" />
                                </div>
                              ) : (
                                districts.map(district => (
                                  <div 
                                    key={district}
                                    className={cn(
                                      "px-2 py-1 cursor-pointer hover:bg-gray-100 rounded text-sm",
                                      npoFilters.district === district && "bg-primary/10 font-medium"
                                    )}
                                    onClick={() => handleFilterChange('district', district)}
                                  >
                                    {district}
                                  </div>
                                ))
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                    
                    {isLoadingNPOs && npos.length === 0 ? (
                      <div className="space-y-4">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="flex items-start gap-4">
                            <Skeleton className="w-4 h-4 rounded-sm mt-1" />
                            <Skeleton className="w-12 h-12 rounded-full" />
                            <div className="flex-1 space-y-2">
                              <Skeleton className="h-5 w-3/4" />
                              <Skeleton className="h-4 w-full" />
                              <Skeleton className="h-4 w-1/2" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : npos.length > 0 ? (
                      <>
                        <div className="space-y-3 mb-4 h-[400px] overflow-y-auto pr-2">
                          {npos.map((npo) => (
                            <NPOCard 
                              key={npo.id} 
                              npo={npo} 
                              isSelected={selectedNPOs.some(item => item.id === npo.id)}
                              onToggleSelect={handleToggleSelectNPO}
                            />
                          ))}
                          
                          {/* Infinite scroll loading indicator */}
                          {(hasNextPage || isFetchingNextPage) && (
                            <div ref={ref} className="flex justify-center py-2">
                              {isFetchingNextPage ? (
                                <div className="flex items-center space-x-2">
                                  <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-primary"></div>
                                  <p className="text-sm text-gray-500">Loading more NPOs...</p>
                                </div>
                              ) : (
                                <div className="h-8"></div> // Invisible element for intersection observer
                              )}
                            </div>
                          )}
                        </div>
                        
                        <Button 
                          className="w-full flex items-center justify-center gap-2"
                          onClick={handleRequestQuote}
                          disabled={selectedNPOs.length === 0}
                        >
                          <Send className="h-4 w-4" />
                          Request Quote {selectedNPOs.length > 0 && `(${selectedNPOs.length} selected)`}
                        </Button>
                      </>
                    ) : (
                      <div className="py-8 text-center">
                        <p className="text-gray-500 mb-4">No NPOs found matching your filters.</p>
                        {hasActiveFilters && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={clearFilters}
                            className="mx-auto"
                          >
                            <X className="h-4 w-4 mr-2" />
                            Clear filters
                          </Button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </Card>
              
              <Card className="p-6">
                <h2 className="text-xl font-semibold mb-4">Need Help?</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Contact our team for assistance in organizing this volunteer opportunity for your group.
                </p>
                <Button className="w-full">Contact Support</Button>
              </Card>
            </div>
          </div>
        </div>
      </div>
      
      {/* RFQ Dialog */}
      <Dialog open={rfqDialogOpen} onOpenChange={setRfqDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Request a Quote</DialogTitle>
            <DialogDescription>
              {selectedNPOs.length > 0 
                ? `Fill out the form below to request a quote from ${selectedNPOs.length} selected NPO${selectedNPOs.length > 1 ? 's' : ''}.`
                : "Fill out the form below to see available NPOs for this opportunity."}
            </DialogDescription>
          </DialogHeader>
          {!session ? (
            <div className="py-6 text-center space-y-4">
              <Lock className="h-12 w-12 mx-auto text-gray-400" />
              <h3 className="text-lg font-medium">Login to Continue</h3>
              <p className="text-sm text-gray-500">You need to login to request quotes from NPOs.</p>
              <Button asChild className="w-full">
                <Link to="/login">Login</Link>
              </Button>
            </div>
          ) : profile?.user_type !== 'company' ? (
            <div className="py-6 text-center space-y-4">
              <Building className="h-12 w-12 mx-auto text-gray-400" />
              <h3 className="text-lg font-medium">Corporate Account Required</h3>
              <p className="text-sm text-gray-500">You need a corporate account to request quotes from NPOs.</p>
              <Button asChild variant="outline" className="w-full">
                <Link to="/profile">Switch Account</Link>
              </Button>
            </div>
          ) : (
            <RFQForm 
              onSubmit={handleRfqSubmit} 
              onClose={() => setRfqDialogOpen(false)} 
              selectedNPOs={selectedNPOs}
              profile={profile}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
